import { CCMS_SDK_VERSION } from './Config';
import { CCMSError } from '../models/common/CCMSError';
import { createCcmsError } from './errorHelpers';
import { InterceptorManager } from './InterceptorManager';
import { normalizeKeys } from '../utils/object';
/**
 * HTTP client for making requests to CCMS APIs
 * Handles authentication, error handling, and interceptors
 */
export class HttpClient {
    constructor(config) {
        this._vendorId = null;
        this.config = config;
        this.interceptors = new InterceptorManager();
        // Add SDK version header interceptor
        this.interceptors.addRequestInterceptor((ctx) => {
            ctx.init.headers = {
                ...ctx.init.headers,
                'X-CCMS-SDK-Version': CCMS_SDK_VERSION
            };
            return ctx;
        });
        // Add X-Vendor-ID header interceptor (single interceptor that reads current vendorId)
        this.interceptors.addRequestInterceptor((ctx) => {
            if (this._vendorId !== null) {
                ctx.init.headers = {
                    ...ctx.init.headers,
                    'X-Vendor-ID': String(this._vendorId)
                };
            }
            return ctx;
        });
    }
    /**
     * Gets the interceptor manager for adding custom interceptors
     */
    getInterceptors() {
        return this.interceptors;
    }
    /**
     * Sets the base URL for API requests
     */
    setBaseUrl(baseUrl) {
        this.config.baseUrl = baseUrl;
    }
    /**
     * Sets the authentication token
     */
    setAuthToken(token) {
        if (token) {
            this.config.getToken = () => Promise.resolve(token);
        }
        else {
            this.config.getToken = () => Promise.resolve(null);
        }
    }
    /**
     * Sets the request timeout in milliseconds
     */
    setTimeout(timeout) {
        this.config.timeout = timeout;
    }
    /**
     * Sets the vendor ID for X-Vendor-ID header on all requests.
     * This is used by vendor portal services to identify the vendor context.
     * @param vendorId - The vendor ID to include in requests, or null to clear
     */
    setVendorId(vendorId) {
        this._vendorId = vendorId;
    }
    /**
     * Gets the current vendor ID
     * @returns The current vendor ID, or null if not set
     */
    getVendorId() {
        return this._vendorId;
    }
    /**
     * Builds request headers including authentication
     */
    async buildHeaders() {
        const headers = {
            'Content-Type': 'application/json'
        };
        // Add API Key if provided
        if (this.config.apiKey) {
            headers['X-API-Key'] = this.config.apiKey;
        }
        // Add JWT token if available
        if (this.config.getToken) {
            const token = await this.config.getToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
        }
        // Add Tenant ID if available
        if (this.config.getTenantId) {
            const tenantId = this.config.getTenantId();
            if (tenantId) {
                headers['X-Tenant-Id'] = tenantId;
            }
        }
        return headers;
    }
    /**
     * Makes an HTTP request to the API
     */
    async request(path, method, body) {
        const url = `${this.config.baseUrl}${path}`;
        const headers = await this.buildHeaders();
        let requestContext = {
            url,
            init: {
                method,
                headers,
                ...(body !== undefined && { body: JSON.stringify(body) })
            }
        };
        // Run request interceptors
        requestContext = await this.interceptors.runRequestInterceptors(requestContext);
        // Execute request with timeout if configured
        const controller = new AbortController();
        const timeoutId = this.config.timeout
            ? setTimeout(() => controller.abort(), this.config.timeout)
            : undefined;
        try {
            const response = await fetch(requestContext.url, {
                ...requestContext.init,
                signal: controller.signal
            });
            if (timeoutId)
                clearTimeout(timeoutId);
            // Parse JSON response
            let data;
            try {
                // Normalize PascalCase keys to camelCase
                data = normalizeKeys(await response.json());
            }
            catch (parseError) {
                throw new CCMSError({
                    code: 'INVALID_RESPONSE',
                    message: 'Failed to parse response as JSON',
                    status: response.status,
                    raw: parseError
                });
            }
            // Validate ResponseData structure
            if (typeof data.success !== 'boolean') {
                throw new CCMSError({
                    code: 'INVALID_RESPONSE',
                    message: 'Response does not conform to ResponseData<T> structure',
                    status: response.status,
                    raw: data
                });
            }
            // Create response context
            let responseContext = {
                data,
                status: response.status,
                headers: response.headers
            };
            // Run response interceptors
            responseContext = await this.interceptors.runResponseInterceptors(responseContext);
            // Check for logical or HTTP failures
            if (!responseContext.data.success || !response.ok) {
                throw createCcmsError(responseContext.data, response.status);
            }
            return responseContext.data;
        }
        catch (error) {
            if (timeoutId)
                clearTimeout(timeoutId);
            if (error instanceof CCMSError) {
                throw error;
            }
            // Handle network errors
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new CCMSError({
                        code: 'TIMEOUT',
                        message: 'Request timeout',
                        status: 408,
                        raw: error
                    });
                }
                throw new CCMSError({
                    code: 'NETWORK_ERROR',
                    message: error.message,
                    raw: error
                });
            }
            throw error;
        }
    }
    /**
     * Makes a raw GET request without ResponseData wrapper
     * Used for endpoints that return raw objects (e.g., health checks)
     */
    async getRaw(path) {
        const url = `${this.config.baseUrl}${path}`;
        const headers = await this.buildHeaders();
        let requestContext = {
            url,
            init: {
                method: 'GET',
                headers
            }
        };
        // Run request interceptors
        requestContext = await this.interceptors.runRequestInterceptors(requestContext);
        // Execute request with timeout if configured
        const controller = new AbortController();
        const timeoutId = this.config.timeout
            ? setTimeout(() => controller.abort(), this.config.timeout)
            : undefined;
        try {
            const response = await fetch(requestContext.url, {
                ...requestContext.init,
                signal: controller.signal
            });
            if (timeoutId)
                clearTimeout(timeoutId);
            // Check HTTP status
            if (!response.ok) {
                throw new CCMSError({
                    code: 'HTTP_ERROR',
                    message: `HTTP ${response.status}: ${response.statusText}`,
                    status: response.status
                });
            }
            // Parse JSON response (raw object, not ResponseData wrapped)
            try {
                const data = normalizeKeys(await response.json());
                return data;
            }
            catch (parseError) {
                throw new CCMSError({
                    code: 'INVALID_RESPONSE',
                    message: 'Failed to parse response as JSON',
                    status: response.status,
                    raw: parseError
                });
            }
        }
        catch (error) {
            if (timeoutId)
                clearTimeout(timeoutId);
            if (error instanceof CCMSError) {
                throw error;
            }
            // Handle network errors
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new CCMSError({
                        code: 'TIMEOUT',
                        message: 'Request timeout',
                        status: 408,
                        raw: error
                    });
                }
                throw new CCMSError({
                    code: 'NETWORK_ERROR',
                    message: error.message,
                    raw: error
                });
            }
            throw error;
        }
    }
    /**
     * Makes a GET request
     */
    async get(path) {
        return this.request(path, 'GET');
    }
    /**
     * Makes a POST request
     */
    async post(path, body) {
        return this.request(path, 'POST', body);
    }
    /**
     * Makes a PUT request
     */
    async put(path, body) {
        return this.request(path, 'PUT', body);
    }
    /**
     * Makes a DELETE request
     */
    async delete(path) {
        return this.request(path, 'DELETE');
    }
    /**
     * Makes a PATCH request
     */
    async patch(path, body) {
        return this.request(path, 'PATCH', body);
    }
    /**
     * Makes a POST request with FormData (for file uploads)
     * Note: Does not set Content-Type header - browser sets it with boundary
     */
    async postFormData(path, formData) {
        const url = `${this.config.baseUrl}${path}`;
        const headers = await this.buildHeaders();
        // Remove Content-Type header - let browser set it with proper boundary for multipart/form-data
        delete headers['Content-Type'];
        let requestContext = {
            url,
            init: {
                method: 'POST',
                headers,
                body: formData
            }
        };
        // Run request interceptors
        requestContext = await this.interceptors.runRequestInterceptors(requestContext);
        // Execute request with timeout if configured
        const controller = new AbortController();
        const timeoutId = this.config.timeout
            ? setTimeout(() => controller.abort(), this.config.timeout)
            : undefined;
        try {
            const response = await fetch(requestContext.url, {
                ...requestContext.init,
                signal: controller.signal
            });
            if (timeoutId)
                clearTimeout(timeoutId);
            // Parse JSON response
            let data;
            try {
                // Normalize PascalCase keys to camelCase
                data = normalizeKeys(await response.json());
            }
            catch (parseError) {
                throw new CCMSError({
                    code: 'INVALID_RESPONSE',
                    message: 'Failed to parse response as JSON',
                    status: response.status,
                    raw: parseError
                });
            }
            // Validate ResponseData structure
            if (typeof data.success !== 'boolean') {
                throw new CCMSError({
                    code: 'INVALID_RESPONSE',
                    message: 'Response does not conform to ResponseData<T> structure',
                    status: response.status,
                    raw: data
                });
            }
            // Create response context
            let responseContext = {
                data,
                status: response.status,
                headers: response.headers
            };
            // Run response interceptors
            responseContext = await this.interceptors.runResponseInterceptors(responseContext);
            // Check for logical or HTTP failures
            if (!responseContext.data.success || !response.ok) {
                throw createCcmsError(responseContext.data, response.status);
            }
            return responseContext.data;
        }
        catch (error) {
            if (timeoutId)
                clearTimeout(timeoutId);
            if (error instanceof CCMSError) {
                throw error;
            }
            // Handle network errors
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new CCMSError({
                        code: 'TIMEOUT',
                        message: 'Request timeout',
                        status: 408,
                        raw: error
                    });
                }
                throw new CCMSError({
                    code: 'NETWORK_ERROR',
                    message: error.message,
                    raw: error
                });
            }
            throw error;
        }
    }
}
//# sourceMappingURL=HttpClient.js.map